// Phase 9A.7 — note mutation sub-hook for usePublicSignalCardWatchlist.
//
// Owns local state + handlers for the 9A.6 private-note feature.
// Composed by `usePublicSignalCardWatchlist`. Pure React + the
// extracted api module — no Supabase calls outside the api module.

import { useCallback, useState } from "react";
import type { SupabaseClient, User } from "@supabase/supabase-js";

import type { Database } from "../db/types.ts";
import { PRIVATE_NOTE_MAX_LENGTH, normalizePrivateNote } from "./public-signal-format.ts";
import { updateSavedSignalNote } from "./public-signal-watchlist-api.ts";

export interface UsePublicSignalNoteMutationsState {
  notes: ReadonlyMap<string, string | null>;
  setNotes: (next: ReadonlyMap<string, string | null>) => void;
  resetNotes: () => void;
  getNote: (signalCardId: string) => string | null;
  saveNote: (signalCardId: string, note: string) => Promise<{ ok: boolean; error?: string }>;
  clearNote: (signalCardId: string) => Promise<{ ok: boolean; error?: string }>;
  noteUpdating: string | null;
  noteError: string | null;
  resetNoteError: () => void;
}

const EMPTY_NOTES_MAP: ReadonlyMap<string, string | null> = new Map();

export function usePublicSignalNoteMutations(args: {
  supabase: SupabaseClient<Database>;
  user: User | null;
  saved: ReadonlySet<string>;
}): UsePublicSignalNoteMutationsState {
  const { supabase, user, saved } = args;
  const [notes, setNotesState] = useState<ReadonlyMap<string, string | null>>(EMPTY_NOTES_MAP);
  const [noteUpdating, setNoteUpdating] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  const setNotes = useCallback(
    (next: ReadonlyMap<string, string | null>): void => setNotesState(next),
    [],
  );
  const resetNotes = useCallback((): void => setNotesState(EMPTY_NOTES_MAP), []);
  const resetNoteError = useCallback((): void => setNoteError(null), []);

  const writeNote = useCallback(
    async (
      signalCardId: string,
      noteValue: string | null,
    ): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "Not signed in." };
      if (!saved.has(signalCardId)) {
        const msg = "Save this signal first to add a private note.";
        setNoteError(msg);
        return { ok: false, error: msg };
      }
      setNoteUpdating(signalCardId);
      setNoteError(null);
      const r = await updateSavedSignalNote(supabase, user.id, signalCardId, noteValue);
      if (!r.ok) {
        setNoteUpdating(null);
        setNoteError(r.error ?? "Could not update note.");
        return { ok: false, error: r.error };
      }
      const next = new Map(notes);
      next.set(signalCardId, noteValue);
      setNotesState(next);
      setNoteUpdating(null);
      return { ok: true };
    },
    [supabase, user, saved, notes],
  );

  const saveNote = useCallback(
    async (signalCardId: string, note: string): Promise<{ ok: boolean; error?: string }> => {
      const normalized = normalizePrivateNote(note);
      if (normalized.tooLong) {
        const msg = `Note is too long (max ${String(PRIVATE_NOTE_MAX_LENGTH)} characters).`;
        setNoteError(msg);
        return { ok: false, error: msg };
      }
      return writeNote(signalCardId, normalized.value);
    },
    [writeNote],
  );

  const clearNote = useCallback(
    async (signalCardId: string): Promise<{ ok: boolean; error?: string }> =>
      writeNote(signalCardId, null),
    [writeNote],
  );

  const getNote = useCallback(
    (signalCardId: string): string | null => notes.get(signalCardId) ?? null,
    [notes],
  );

  return {
    notes,
    setNotes,
    resetNotes,
    getNote,
    saveNote,
    clearNote,
    noteUpdating,
    noteError,
    resetNoteError,
  };
}
